import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Legend from './components/Legend';
import ShiftModal from './components/ShiftModal';
import SearchBar from './components/SearchBar';
import ViewToggle from './components/ViewToggle';
import DayNav from './components/DayNav';

import {
    GET_LIST,
    UPDATE,
    CREATE,
    DELETE,
    provider,
    history,
} from '../_utils';

import moment from 'moment';
import Utils, { shiftKindKey, nowFraction, toWeekInterval, toMonthInterval, buildSchedulePayload } from './utils';

import DayView from './components/DayView';
import ResourcesTable from './components/ResourcesTable';
import { Modal, OperatorInfoModal, LoadingStub } from '../_Components';
import DaySettingsModal from './components/DaySettingsModal';

import 'moment/locale/ru';
import './weekView.css';
moment.locale('ru');

// Преобразует плоскую структуру needed обратно в объект для сохранения
const denormalizeNeededFromForm = (neededObj) => {
    if (!neededObj || typeof neededObj !== 'object') return null;
    
    const result = {};
    Object.keys(neededObj).forEach(hour => {
        const value = neededObj[hour];
        if (value !== null && value !== undefined && value !== '') {
            result[String(hour)] = String(value);
        }
    });
    
    return Object.keys(result).length > 0 ? result : null;
};

const ScheduleShow = () => {
    const { date } = useParams();
    
    const startDate = moment(date).startOf('day');
    const [operators, setOperators] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [highlightedProjectId, setHighlightedProjectId] = useState(null);
    const [daySettingsModalOpen, setDaySettingsModalOpen] = useState(false);
    const [daySettingsInvalid, setDaySettingsInvalid] = useState(false);
    const [holidayInfo, setHolidayInfo] = useState(null); // Информация о празднике/рабочем выходном
    
    const curDate = startDate.format('YYYY-MM-DD');

    // Общая функция для загрузки данных проектов
    const loadProjectsData = useCallback(async (dateParam) => {
        const currentStartDate = moment(dateParam).startOf('day');
        const currentEndDate = moment(dateParam).endOf('day');
        const dayNum = currentStartDate.day() === 0 ? 7 : currentStartDate.day();
        const dateStr = currentStartDate.format('YYYY-MM-DD');
        
        // Загружаем данные параллельно
        const [holidaysResult, schedulesResult, campaignsResult] = await Promise.all([
            provider(GET_LIST, '/holidays', {
                filter: {
                    date: {
                        $gte: currentStartDate.toDate(),
                        $lte: currentEndDate.toDate(),
                    },
                },
                pagination: { page: 1, perPage: 1 },
            }).catch(() => ({ data: [] })),
            provider(GET_LIST, '/project-day-schedules', {
                filter: { date: dateStr },
                pagination: { page: 1, perPage: 1000 },
            }).catch(() => ({ data: [] })),
            provider(GET_LIST, '/campaigns', {
                filter: { status: 'Active' },
                sort: { field: 'name', order: 'DESC' },
                pagination: { page: 1, perPage: 1000 },
            })
        ]);
        
        const holidaysData = holidaysResult.data || [];
        const projectSchedulesData = schedulesResult.data || [];
        const campaignsData = campaignsResult.data || [];
        
        // Определяем день недели для графика (с учетом праздника/рабочего выходного)
        let holidayData = holidaysData.length > 0 ? holidaysData[0] : null;
        let effectiveDayNum = dayNum;
        if (holidayData) {
            if (holidayData.type === 'holiday' && holidayData.scheduleDay) {
                effectiveDayNum = holidayData.scheduleDay;
            } else if (holidayData.type === 'working_weekend' && holidayData.scheduleDay) {
                effectiveDayNum = holidayData.scheduleDay;
            }
        }
        
        // Создаем Map для быстрого поиска: projectId -> daySchedule
        const schedulesMap = new Map();
        projectSchedulesData.forEach(s => {
            if (!s || !s.projectId) return;
            const scheduleProjectId = typeof s.projectId === 'object' 
                ? (s.projectId?._id || s.projectId?.id) 
                : s.projectId;
            schedulesMap.set(scheduleProjectId, s);
        });
        
        const projectsList = campaignsData
            .filter(p => p.schedule && p.schedule[effectiveDayNum])
            .map(p => {
                const daySchedule = schedulesMap.get(p._id);
                let schedule = p.schedule[effectiveDayNum];
                let isActive = true;
                
                if (daySchedule) {
                    if (daySchedule.active !== false) {
                        schedule = {
                            start: daySchedule.start,
                            end: daySchedule.end,
                            needed: daySchedule.needed !== undefined ? daySchedule.needed : (schedule?.needed || null),
                        };
                        isActive = true;
                    } else {
                        isActive = false;
                    }
                } else {
                    if (schedule && schedule.needed === undefined) {
                        schedule.needed = null;
                    }
                }
                
                return {
                    name: p.name,
                    schedule: schedule,
                    operators: p.operators || [],
                    color: p.color || '#2196F3',
                    _id: p._id,
                    active: isActive,
                };
            })
            .filter(p => p.active !== false);
        
        return { projectsList, holidayData };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadData = async () => {
            setLoading(true);
            try {
                const currentStartDate = moment(date).startOf('day');
                const currentEndDate = moment(date).endOf('day');
                
                // Загружаем операторов и данные проектов параллельно
                const [operatorsResult, projectsResult, scheduleResult] = await Promise.all([
                    provider(GET_LIST, '/operators', {
                        filter: { status: 'Active', type: {$in: ["operator", "leader", "senior", "test"]}},
                        sort: { field: 'login', order: 'DESC' },
                        pagination: { page: 1, perPage: 1000 },
                    }),
                    loadProjectsData(date),
                    provider(GET_LIST, '/schedule', {
                        filter: { startDate: currentStartDate, endDate: currentEndDate },
                        sort: { field: 'date', order: 'DESC' },
                        pagination: { page: 1, perPage: 1000 },
                    })
                ]);
                
                const operatorsList = operatorsResult.data.map(e => Utils.formatOperator({
                    ...e,
                    type: e.type
                }));
                
                if (isMounted) {
                    setOperators(operatorsList);
                    setProjects(projectsResult.projectsList);
                    setSchedule(scheduleResult.data);
                    setHolidayInfo(projectsResult.holidayData);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error loading data:', error);
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        loadData();
        return () => {
            isMounted = false;
        };
    }, [date, loadProjectsData]);

    const handleCellClick = useCallback((operator, date, shift) => {
        setSelectedOperator(operator);
        setSelectedDate(date);
        setSelectedItem(shift);
        setModalOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setSelectedOperator(null);
        setSelectedDate(null);
        setSelectedItem(null);
        setModalOpen(false);
    }, []);

    const handleDaySettingsClose = useCallback(() => {
        setDaySettingsModalOpen(false);
        setDaySettingsInvalid(false);
    }, []);

    const reloadDay = useCallback(async () => {
        const currentStartDate = moment(date).startOf('day');
        const currentEndDate = moment(date).endOf('day');
        const { data: scheduleData } = await provider(
            GET_LIST,
            '/schedule',
            {
                filter: { startDate: currentStartDate, endDate: currentEndDate },
                sort: { field: 'date', order: 'DESC' },
                pagination: { page: 1, perPage: 1000 },
            }
        );
        setSchedule(scheduleData);
    }, [date]);

    const handleSave = useCallback(async (payload) => {
        try {
            if (selectedItem?._id || selectedItem?.id) {
                await provider(UPDATE, '/schedule', {
                    id: selectedItem._id || selectedItem.id,
                    data: payload
                });
            } else {
                await provider(CREATE, '/schedule', { data: payload });
            }

            handleClose();
            await reloadDay();
        } catch (error) {
            console.error('Error saving schedule:', error);
        }
    }, [selectedItem, handleClose, reloadDay]);

    const handleShiftTimes = useCallback(async (operator, shiftDate, shift, start, end) => {
        const itemId = shift && (shift._id || shift.id);
        if (!itemId) {
            return;
        }
        setSchedule((current) => (current || []).map((item) => {
            if ((item._id || item.id) !== itemId) {
                return item;
            }
            const schedules = (item.schedules || []).map((slot, index) => (
                index === 0 ? { ...slot, start, end } : slot
            ));
            return { ...item, schedules };
        }));
        try {
            await provider(UPDATE, '/schedule', {
                id: itemId,
                data: buildSchedulePayload({
                    operator,
                    date: shiftDate,
                    type: shift.type,
                    change: shift.change,
                    start,
                    end,
                    description: shift.description || 'Сдвиг смены',
                }),
            });
        } catch (error) {
            console.error('Error moving shift:', error);
            await reloadDay();
        }
    }, [reloadDay]);

    const handleDelete = useCallback(async (itemId) => {
        try {
            if (itemId) {
                await provider(DELETE, '/schedule', { id: itemId });
            }
            handleClose();
            // Перезагружаем данные через обновление состояния
            const currentStartDate = moment(date).startOf('day');
            const currentEndDate = moment(date).endOf('day');
            const { data: scheduleData } = await provider(
                GET_LIST,
                '/schedule',
                {
                    filter: { startDate: currentStartDate, endDate: currentEndDate },
                    sort: { field: 'date', order: 'DESC' },
                    pagination: { page: 1, perPage: 1000 },
                }
            );
            setSchedule(scheduleData);
        } catch (error) {
            console.error('Error deleting schedule:', error);
        }
    }, [handleClose, date]);

    const onSaveSettings = async (values) => {
        try {
            // setLoading(true);
            const dateStr = moment(date).format('YYYY-MM-DD');
            
            // Сохраняем или обновляем праздник
            if (values.type !== 'normal') {
                const holidayData = {
                    date: dateStr,
                    name: values.name || (values.type === 'holiday' ? 'Праздничный день' : 'Рабочий выходной'),
                    type: values.type,
                    scheduleDay: values.scheduleDay,
                };

                if (holidayInfo?._id) {
                    await provider(UPDATE, '/holidays', {
                        id: holidayInfo._id,
                        data: holidayData,
                    });
                } else {
                    await provider(CREATE, '/holidays', { data: holidayData });
                }
            } else {
                if (holidayInfo?._id) {
                    // Извлекаем строковое значение ID (может быть объектом MongoDB ObjectId)
                    const holidayId = typeof holidayInfo._id === 'object' 
                        ? (holidayInfo._id.toString ? holidayInfo._id.toString() : holidayInfo._id._id || holidayInfo._id.id)
                        : holidayInfo._id;
                    await provider(DELETE, '/holidays', { id: String(holidayId) });
                }
            }

            // Сохраняем графики проектов
            if (values.projects && values.projects.length > 0) {
                const schedulesToSave = values.projects
                    .filter(p => p.projectId)
                    .map(p => ({
                        projectId: p.projectId,
                        start: p.start || 540,
                        end: p.end || 1080,
                        active: p.active !== false,
                        needed: denormalizeNeededFromForm(p.needed),
                    }));

                if (schedulesToSave.length > 0) {
                    const batchData = {
                        date: dateStr,
                        schedules: schedulesToSave,
                    };
                    await provider(CREATE, '/project-day-schedules/batch', { data: batchData });
                }
            }
        } catch (error) {
            console.error('Error saving day settings:', error);
        } finally {
            await reloadProjectsData();
            setDaySettingsModalOpen(false);
        }
    };

    const createClick = useCallback(() => {
        setSelectedOperator(null);
        setSelectedDate(startDate);
        setSelectedItem(null);
        setModalOpen(true);
    }, [startDate]);

    const goToDate = useCallback((nextDate) => {
        history.push(`/schedule/${moment(nextDate).format('YYYY-MM-DD')}/show`);
    }, []);

    const goToList = useCallback((nextView) => {
        const pivot = moment(date);
        const nextInterval = nextView === 'month' ? toMonthInterval(pivot) : toWeekInterval(pivot);
        let hideEmpty = true;
        try {
            const saved = localStorage.getItem('schedule_filter');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.hideEmpty !== undefined) hideEmpty = parsed.hideEmpty;
            }
        } catch (error) {
            console.error('Error restoring filter:', error);
        }
        const filterObj = {
            view: nextView,
            interval: {
                startDate: moment(nextInterval.startDate).format('YYYY-MM-DD'),
                endDate: moment(nextInterval.endDate).format('YYYY-MM-DD'),
                ...(nextInterval.anchor ? { anchor: nextInterval.anchor } : {}),
            },
            hideEmpty,
        };
        const filterString = JSON.stringify(filterObj);
        try {
            localStorage.setItem('schedule_filter', filterString);
        } catch (error) {
            console.error('Error saving filter:', error);
        }
        history.push(`/schedule?filter=${encodeURIComponent(filterString)}`);
    }, [date]);

    const handleProjectClick = useCallback((projectId) => {
        setHighlightedProjectId((current) => (current === projectId ? null : projectId));
    }, []);

    const [searchQuery, setSearchQuery] = useState('');
    const [kindFilter, setKindFilter] = useState([]);
    const [operatorInfoOpen, setOperatorInfoOpen] = useState(false);
    const [selectedOperatorId, setSelectedOperatorId] = useState(null);
    const boardRef = useRef(null);
    const [nowLeft, setNowLeft] = useState(null);
    const [nowLabel, setNowLabel] = useState('');

    const placeNow = useCallback(() => {
        if (!boardRef.current || !moment(date).isSame(moment(), 'day')) {
            setNowLeft(null);
            return;
        }
        const frac = nowFraction();
        if (frac == null) {
            setNowLeft(null);
            return;
        }
        const nameW = parseFloat(getComputedStyle(boardRef.current).getPropertyValue('--name')) || 196;
        const width = boardRef.current.clientWidth - nameW;
        setNowLeft(nameW + width * frac);
        setNowLabel(moment().format('HH:mm'));
    }, [date]);

    useEffect(() => {
        placeNow();
        window.addEventListener('resize', placeNow);
        const timer = setInterval(placeNow, 60000);
        return () => {
            window.removeEventListener('resize', placeNow);
            clearInterval(timer);
        };
    }, [placeNow, loading]);

    const reloadProjectsData = useCallback(async () => {
        const { projectsList, holidayData } = await loadProjectsData(date);
        setProjects(projectsList);
        setHolidayInfo(holidayData);
    }, [date, loadProjectsData]);

    const kindCounts = useMemo(() => {
        const counts = {};
        (schedule || []).forEach((shift) => {
            const key = shiftKindKey(shift);
            if (key) counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [schedule]);

    const toggleKind = useCallback((key) => {
        setKindFilter((current) => (
            current.includes(key) ? current.filter((item) => item !== key) : current.concat(key)
        ));
    }, []);

    if (loading) {
        return (
            <div className="pc-page pc-sched">
                <LoadingStub title="Загружаем расписание" />
            </div>
        );
    }

    return (
        <div className="pc-page pc-sched">
            <div className="pc-sched-topbar">
                <h1>Расписание</h1>
                <ViewToggle
                    mode="day"
                    onDay={() => {}}
                    onWeek={() => goToList('week')}
                    onMonth={() => goToList('month')}
                />
                <DayNav
                    date={startDate}
                    onPrev={() => goToDate(moment(date).subtract(1, 'day'))}
                    onNext={() => goToDate(moment(date).add(1, 'day'))}
                    onToday={() => goToDate(moment())}
                />
                <SearchBar value={searchQuery} onSearch={setSearchQuery} placeholder="Поиск оператора" />
                <div className="pc-sched-end">
                    <Legend counts={kindCounts} active={kindFilter} onToggle={toggleKind} workOnly />
                    <button type="button" className="pc-sched-btn pc-sched-btn--pri" onClick={createClick}>
                        Смена
                    </button>
                </div>
            </div>
            <div className="pc-day-scroll">
                <div className="pc-day-board" ref={boardRef}>
                    <ResourcesTable
                        projects={projects}
                        schedule={schedule}
                        date={curDate}
                        highlightedProjectId={highlightedProjectId}
                        onProjectClick={handleProjectClick}
                        onClear={() => setHighlightedProjectId(null)}
                        onSettings={() => setDaySettingsModalOpen(true)}
                    />
                    <div className="pc-day-gap" />
                    <DayView
                        operators={operators}
                        schedule={schedule}
                        date={startDate}
                        onCellClick={handleCellClick}
                        onShiftTimes={handleShiftTimes}
                        searchQuery={searchQuery}
                        highlightedProjectId={highlightedProjectId}
                        projects={projects}
                        kindFilter={kindFilter}
                        onOperatorClick={(id) => {
                            setSelectedOperatorId(id);
                            setOperatorInfoOpen(true);
                        }}
                    />
                    {nowLeft != null && (
                        <div className="pc-day-now" style={{ left: nowLeft }} data-t={nowLabel} />
                    )}
                </div>
            </div>

            <ShiftModal
                open={modalOpen}
                operator={selectedOperator}
                operators={operators}
                date={selectedDate || startDate.toDate()}
                shift={selectedItem}
                onClose={handleClose}
                onSave={handleSave}
                onDelete={handleDelete}
            />
            <OperatorInfoModal
                open={operatorInfoOpen}
                onClose={() => {
                    setOperatorInfoOpen(false);
                    setSelectedOperatorId(null);
                }}
                operatorId={selectedOperatorId}
            />

            {/* Модальное окно для настройки дня */}
            {daySettingsModalOpen && (
                <Modal
                    open={daySettingsModalOpen}
                    handleClose={handleDaySettingsClose}
                    handleSaveClick={onSaveSettings}
                    handleCloseClick={handleDaySettingsClose}
                    title={`Настройки дня: ${moment(date).format('DD.MM.YYYY')}`}
                    width="lg"
                    saveDisabled={daySettingsInvalid}
                    fields={
                        <DaySettingsModal
                            key={`day-settings-${curDate}`} // Ключ для принудительного пересоздания при открытии
                            date={startDate.toDate()}
                            projects={projects}
                            onClose={handleDaySettingsClose}
                            onInvalidChange={setDaySettingsInvalid}
                            onSave={async () => {
                                await reloadProjectsData();
                                handleDaySettingsClose();
                            }}
                        />
                    }
                />
            )}
        </div>
    );
};

export default ScheduleShow;
